import { OrderedSet, Segmentation } from '../../../mol-data/int';
import { Structure, StructureElement, StructureProperties, Unit } from '../../../mol-model/structure';
import { MoleculeType } from '../../../mol-model/structure/model/types';
import { residueEntityType, residueFromLocation } from '../identity';
import { PluginContext } from '../../../mol-plugin/context';
import { collapseResidueRanges, ExpandResultCache, failure, pagedResult, success, toMcpResult } from '../result';
import { ToolDefinition, ToolResponse, WebMcpRequest, WebMcpValue } from '../registration';
import { createSelectorResolverContext, resolveSelector, selectorLimits, selectorQueryNames, selectorRequestSchema } from '../selector';
import { Atom, Residue, StructureSummary } from '../types';
import { runWebMcpMutation, WebMcpMutationServices } from './safety';

interface DetailInput {
    readonly selector?: WebMcpValue
    readonly detail?: WebMcpValue
}

function detailInput(input: WebMcpRequest): DetailInput { return input; }

function loaded(plugin: PluginContext) {
    return plugin.managers.structure.hierarchy.current.structures.flatMap(item => {
        const object = item.cell.obj;
        return object ? [{ ref: item.cell.transform.ref, structure: object.data, label: object.label }] : [];
    });
}

function residue(ref: string, location: StructureElement.Location): Residue | undefined {
    if (!Unit.isAtomic(location.unit)) return void 0;
    return residueFromLocation(ref, location);
}

function residues(ref: string, loci: StructureElement.Loci): readonly Residue[] {
    const values = new Map<string, Residue>();
    StructureElement.Loci.forEachLocation(loci, location => {
        const value = residue(ref, location);
        if (value) values.set(`${value.id}/${value.insCode ?? ''}`, value);
    });
    return [...values.values()];
}

function atoms(ref: string, loci: StructureElement.Loci): readonly Atom[] {
    const values: Atom[] = [];
    StructureElement.Loci.forEachLocation(loci, location => {
        const parent = residue(ref, location);
        if (!parent) return;
        const value: Atom = { ...parent, atom: StructureProperties.atom.label_atom_id(location), element: StructureProperties.atom.type_symbol(location), x: StructureProperties.atom.x(location), y: StructureProperties.atom.y(location), z: StructureProperties.atom.z(location) };
        const altId = StructureProperties.atom.label_alt_id(location);
        if (altId) value.altId = altId;
        values.push(value);
    });
    return values;
}

function selectedResidues(plugin: PluginContext): readonly Residue[] {
    return loaded(plugin).flatMap(entry => {
        const loci = plugin.managers.structure.selection.getLoci(entry.structure);
        return StructureElement.Loci.is(loci) ? residues(entry.ref, loci) : [];
    });
}

type CountCategory = 'polymer' | 'ligand' | 'branched' | 'ion' | 'water';

function moleculeCategory(entityType: string | undefined, moleculeType: MoleculeType): CountCategory {
    const category = residueEntityType(entityType, moleculeType);
    return category === 'unknown' ? 'ligand' : category;
}

export function structureCounts(structure: Structure): Omit<StructureSummary, 'ref' | 'label' | 'modelCount'> {
    const chainCounts: Record<CountCategory, number> = { polymer: 0, ligand: 0, branched: 0, ion: 0, water: 0 };
    const residueCounts: Record<CountCategory, number> = { polymer: 0, ligand: 0, branched: 0, ion: 0, water: 0 };
    const coarseChains = new Set<string>(), coarseResidues = new Set<string>();
    for (const unit of structure.units) {
        if (Unit.isAtomic(unit)) {
            const hierarchy = unit.model.atomicHierarchy;
            const unitChains: Record<CountCategory, Set<number>> = { polymer: new Set(), ligand: new Set(), branched: new Set(), ion: new Set(), water: new Set() };
            const residueIt = Segmentation.transientSegments(hierarchy.residueAtomSegments, unit.elements);
            while (residueIt.hasNext) {
                const residueIndex = residueIt.move().index;
                const element = hierarchy.residueAtomSegments.offsets[residueIndex];
                const chainIndex = hierarchy.chainAtomSegments.index[element];
                const entityId = hierarchy.chains.label_entity_id.value(chainIndex);
                const entityIndex = unit.model.entities.getEntityIndex(entityId);
                const entityType = entityIndex < 0 ? void 0 : unit.model.entities.data.type.value(entityIndex);
                const category = moleculeCategory(entityType, hierarchy.derived.residue.moleculeType[residueIndex]);
                unitChains[category].add(chainIndex);
                residueCounts[category]++;
            }
            for (const category of ['polymer', 'ligand', 'branched', 'ion', 'water'] as const) chainCounts[category] += unitChains[category].size;
            continue;
        }
        OrderedSet.forEach(unit.elements, element => {
            const chainIndex = unit.coarseElements.chainElementSegments.index[element];
            coarseChains.add(`${unit.id}:${chainIndex}`);
            const begin = unit.coarseElements.seq_id_begin.value(element), end = unit.coarseElements.seq_id_end.value(element);
            for (let seq = begin; seq <= end; seq++) coarseResidues.add(`${unit.id}:${chainIndex}:${seq}`);
        });
    }
    const polymerChainCount = chainCounts.polymer + coarseChains.size;
    const ligandChainCount = chainCounts.ligand;
    const branchedChainCount = chainCounts.branched;
    const ionChainCount = chainCounts.ion;
    const waterChainCount = chainCounts.water;
    const polymerResidueCount = residueCounts.polymer + coarseResidues.size;
    const ligandResidueCount = residueCounts.ligand;
    const branchedResidueCount = residueCounts.branched;
    const ionResidueCount = residueCounts.ion;
    const waterResidueCount = residueCounts.water;
    return {
        chainCount: polymerChainCount + ligandChainCount + branchedChainCount + ionChainCount + waterChainCount,
        residueCount: polymerResidueCount + ligandResidueCount + branchedResidueCount + ionResidueCount + waterResidueCount,
        polymerChainCount, ligandChainCount, branchedChainCount, ionChainCount, waterChainCount,
        polymerResidueCount, ligandResidueCount, branchedResidueCount, ionResidueCount, waterResidueCount,
    };
}

function structureSummaries(plugin: PluginContext): readonly StructureSummary[] {
    return loaded(plugin).map(entry => ({ ref: entry.ref, label: entry.label, modelCount: entry.structure.models.length, ...structureCounts(entry.structure) }));
}

function resolve(plugin: PluginContext, value: WebMcpValue | undefined) {
    return resolveSelector(value, createSelectorResolverContext(plugin));
}

function selectionResult(summary: string, values: readonly Residue[], cache: ExpandResultCache) {
    return pagedResult(cache, values, page => success(summary, { type: 'selection', residues: page.items, ranges: collapseResidueRanges(page.items) }, page.truncation));
}

function atomResult(summary: string, values: readonly Atom[], cache: ExpandResultCache) {
    return pagedResult(cache, values, page => success(summary, { type: 'atoms', atoms: page.items }, page.truncation));
}

function structureRef(plugin: PluginContext, loci: StructureElement.Loci): string | undefined {
    const parent = plugin.helpers.substructureParent.get(loci.structure)?.obj?.data;
    return loaded(plugin).find(entry => entry.structure === (parent ?? loci.structure))?.ref;
}

const GroundingSelectorSchema = selectorRequestSchema();

async function mutate(services: WebMcpMutationServices, tool: string, target: string | undefined, summary: string, execute: () => void): Promise<ToolResponse> {
    const outcome = await runWebMcpMutation({ actor: 'agent', tool, args: { action: tool, target }, summary }, services, async () => { execute(); });
    return outcome.ok ? toMcpResult(success(outcome.summary, { type: 'mutation', action: tool, target })) : toMcpResult(failure(outcome.error, outcome.hint));
}

/** Grounding tools return canonical author and label identifiers from real Mol* loci. */
export function createGroundingTools(plugin: PluginContext, cache: ExpandResultCache, services: WebMcpMutationServices): readonly ToolDefinition[] {
    return [
        {
            name: 'get-selector-guide',
            description: 'Return the selector forms accepted by Mol* WebMCP tools.',
            inputSchema: { type: 'object', additionalProperties: false },
            annotations: { readOnlyHint: true },
            execute: () => toMcpResult(success('Selector guide returned.', {
                type: 'selector-guide',
                numbering: 'Use auth numbering by default; set numbering to label only when using label_asym_id and label_seq_id.',
                forms: [
                    'current-selection | current-focus | hovered: { kind }',
                    'structure: { kind, ref }',
                    'chain: { kind, chain, ref?, numbering? }',
                    'residues: { kind, ranges: [{ chain, from, to?, insCode?, ref?, numbering? }] }',
                    'atoms: { kind, atoms: [{ chain, seq, atom, insCode?, ref?, numbering? }] }',
                    'named: { kind, query }',
                    'around: { kind, of: selector, radius, wholeResidues? }',
                    'union | intersect | subtract: { kind, of: [selector, ...] }',
                ],
                namedQueries: selectorQueryNames(),
                limits: selectorLimits,
                multiStructure: 'Set ref on structure-local chain, residue, and atom selectors when more than one structure is loaded.',
            }))
        },
        { name: 'get-selection', description: 'Return the current selection as canonical residues.', inputSchema: { type: 'object', properties: { detail: { type: 'string', enum: ['residues', 'atoms'] } } }, annotations: { readOnlyHint: true }, execute: input => {
            const request = detailInput(input);
            const values = selectedResidues(plugin);
            if (values.length === 0) return toMcpResult(failure('Nothing is selected.', 'Use set-selection with a selector, then retry.'));
            if (request.detail === 'atoms') {
                const values = loaded(plugin).flatMap(entry => {
                    const loci = plugin.managers.structure.selection.getLoci(entry.structure);
                    return StructureElement.Loci.is(loci) ? atoms(entry.ref, loci) : [];
                });
                if (values.length > 500) return toMcpResult(failure('Atom detail exceeds the 500-atom threshold.', 'Request residue detail, or narrow the selection before requesting atoms.'));
                return atomResult('Current selection atoms.', values, cache);
            }
            return selectionResult('Current selection.', values, cache);
        } },
        { name: 'get-focus', description: 'Return the current focus as canonical residues.', inputSchema: { type: 'object' }, annotations: { readOnlyHint: true }, execute: () => {
            const current = plugin.managers.structure.focus.current;
            if (!current) return toMcpResult(failure('Nothing is focused.', 'Use set-focus with a selector, then retry.'));
            const ref = structureRef(plugin, current.loci);
            return ref ? selectionResult('Current focus.', residues(ref, current.loci), cache) : toMcpResult(failure('Focused structure is no longer loaded.', 'Set focus again on a loaded structure.'));
        } },
        { name: 'get-hovered', description: 'Return the currently hovered residue or atom.', inputSchema: { type: 'object', properties: { detail: { type: 'string', enum: ['residues', 'atoms'] } } }, annotations: { readOnlyHint: true }, execute: input => {
            const hovered = plugin.behaviors.interaction.hover.value.current.loci;
            if (!StructureElement.Loci.is(hovered) || StructureElement.Loci.isEmpty(hovered)) return toMcpResult(failure('Nothing is hovered.', 'Move the pointer over a structure, then retry.'));
            const ref = structureRef(plugin, hovered);
            if (!ref) return toMcpResult(failure('Hovered structure is no longer loaded.', 'Move the pointer over a loaded structure.'));
            const hoveredAtoms = atoms(ref, hovered);
            if (detailInput(input).detail === 'atoms' && hoveredAtoms.length > 500) return toMcpResult(failure('Atom detail exceeds the 500-atom threshold.', 'Request residue detail, or narrow the hover target.'));
            return detailInput(input).detail === 'atoms' ? atomResult('Hovered atoms.', hoveredAtoms, cache) : selectionResult('Hovered residues.', residues(ref, hovered), cache);
        } },
        { name: 'list-structures', description: 'List loaded structure references and sizes.', inputSchema: { type: 'object' }, annotations: { readOnlyHint: true }, execute: () => {
            const values = structureSummaries(plugin);
            return values.length ? pagedResult(cache, values, page => success(`${values.length} structure(s) loaded.`, { type: 'structures', structures: page.items }, page.truncation)) : toMcpResult(failure('No structures are loaded.', 'Load a structure before requesting structure details.'));
        } },
        { name: 'get-capabilities', description: 'Report currently available WebMCP viewer capabilities.', inputSchema: { type: 'object' }, annotations: { readOnlyHint: true }, execute: () => toMcpResult(success('Viewer capabilities.', { type: 'capabilities', structures: loaded(plugin).length > 0, volumes: plugin.managers.volume.hierarchy.current.volumes.length > 0, camera: plugin.canvas3d !== void 0, hovered: StructureElement.Loci.is(plugin.behaviors.interaction.hover.value.current.loci) })) },
        { name: 'get-visible-state', description: 'Return visibility state for loaded structures.', inputSchema: { type: 'object' }, annotations: { readOnlyHint: true }, execute: () => {
            const structures = plugin.managers.structure.hierarchy.current.structures.map(item => ({ ref: item.cell.transform.ref, visible: !item.cell.state.isHidden }));
            return pagedResult(cache, structures, page => success('Structure visibility state.', { type: 'visible-state', structures: page.items }, page.truncation));
        } },
        { name: 'highlight-residues', description: 'Highlight a selector without changing selection.', inputSchema: GroundingSelectorSchema, execute: async input => {
            const target = resolve(plugin, input.selector);
            if (!target.ok) return toMcpResult(target);
            const entries = target.entries.filter(entry => !StructureElement.Loci.isEmpty(entry.loci));
            const first = entries[0];
            if (!first) return toMcpResult(failure('The selector matched no atoms.', 'Choose residues in a loaded structure.'));
            return mutate(services, 'highlight-residues', first.ref, 'Residues highlighted.', () => {
                plugin.managers.interactivity.lociHighlights.highlightOnly({ loci: first.loci }, false);
                for (const entry of entries.slice(1)) plugin.managers.interactivity.lociHighlights.highlight({ loci: entry.loci }, false);
            });
        } },
        { name: 'clear-highlight', description: 'Clear the WebMCP highlight.', inputSchema: { type: 'object' }, execute: () => mutate(services, 'clear-highlight', void 0, 'Highlight cleared.', () => plugin.managers.interactivity.lociHighlights.clearHighlights()) },
    ];
}
